import React, { useState, useEffect } from 'react';
import { cn } from "../lib/utils";

const OptimizedImage = ({ 
  src, 
  alt, 
  width, 
  height, 
  className = "", 
  lazy = true,
  placeholder = "blur", // "blur", "color", or "image"
  placeholderSrc = "/images/placeholder.png", // Used when placeholder="image"
  darkModeAdjust = false,
  fallbackSrc
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [placeholderColor, setPlaceholderColor] = useState("#e5e7eb");
  const [imageSrc, setImageSrc] = useState(src);
  
  useEffect(() => {
    // Reset states when src changes
    setIsLoaded(false);
    setHasError(false);
    setImageSrc(src);
    
    // For a real implementation, you'd generate this server-side
    // This is a simplified placeholder generation
    if (placeholder === "color") {
      setPlaceholderColor(generatePlaceholderColor());
    }
  }, [placeholder, src]);
  
  const generatePlaceholderColor = () => {
    // Simple hash function to generate a color from the src
    let hash = 0;
    for (let i = 0; i < src.length; i++) {
      hash = src.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Convert to hex color
    const color = (hash & 0x00FFFFFF).toString(16).toUpperCase();
    return `#${"00000".substring(0, 6 - color.length)}${color}`;
  };
  
  const handleError = () => {
    setHasError(true);
    if (fallbackSrc) {
      setImageSrc(fallbackSrc);
    }
  };
  
  return (
    <div 
      className={cn(
        "relative overflow-hidden",
        className
      )}
      style={{
        width: width ? `${width}px` : '100%',
        height: height ? `${height}px` : 'auto',
        backgroundColor: placeholder === "image" ? "transparent" : placeholderColor,
      }}
    >
      {/* Placeholder Image */}
      {placeholder === "image" && !isLoaded && (
        <img
          src={placeholderSrc}
          alt="Loading"
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden="true"
        />
      )}
      
      {/* Actual Image */}
      <img
        src={imageSrc}
        alt={alt}
        width={width}
        height={height}
        loading={lazy ? "lazy" : "eager"}
        onLoad={() => setIsLoaded(true)}
        onError={handleError}
        className={cn(
          "w-full h-full object-cover transition-opacity duration-300",
          isLoaded ? 'opacity-100' : 'opacity-0',
          darkModeAdjust && "dark:brightness-90 dark:contrast-110"
        )}
      />
    </div>
  );
};

export default OptimizedImage;
